import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useVehiclesList } from '@/features/vehicles/api';
import { useDriversList } from '@/features/drivers/api';
import { useCreateFine } from './api';
import { SEVERITY_LABELS, type FineSeverity } from './types';

const SEVERITIES: FineSeverity[] = ['light', 'medium', 'serious', 'very_serious'];

const SUGGESTED_POINTS: Record<FineSeverity, number> = {
  light: 3,
  medium: 4,
  serious: 5,
  very_serious: 7,
};

function parseBRLToCents(value: string): number {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const n = Number.parseFloat(normalized || '0');
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

interface FineFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FineFormDialog({ open, onOpenChange }: FineFormDialogProps) {
  const [vehicleId, setVehicleId] = useState('');
  const [responsibility, setResponsibility] = useState<'company' | 'driver'>('company');
  const [driverId, setDriverId] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<FineSeverity>('medium');
  const [points, setPoints] = useState<string>('4');
  const [amountReais, setAmountReais] = useState('');
  const [discountReais, setDiscountReais] = useState('');
  const [infractionDate, setInfractionDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [location, setLocation] = useState('');
  const [infractionCode, setInfractionCode] = useState('');
  const [notes, setNotes] = useState('');

  const vehiclesQuery = useVehiclesList({ limit: 100, active: 'true', sort: 'plate' });
  const driversQuery = useDriversList({ limit: 100, active: 'true', sort: 'name' });
  const create = useCreateFine();

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setVehicleId('');
      setResponsibility('company');
      setDriverId('');
      setDescription('');
      setSeverity('medium');
      setPoints(String(SUGGESTED_POINTS.medium));
      setAmountReais('');
      setDiscountReais('');
      setInfractionDate(today);
      setDueDate('');
      setLocation('');
      setInfractionCode('');
      setNotes('');
    }
  }, [open]);

  // Sugere pontos ao mudar gravidade (sobrescreve só se o user não digitou nada custom)
  const handleSeverityChange = (v: FineSeverity) => {
    setSeverity(v);
    setPoints(String(SUGGESTED_POINTS[v]));
  };

  const canSubmit =
    vehicleId.length > 0 &&
    description.trim().length > 0 &&
    amountReais.length > 0 &&
    infractionDate.length > 0 &&
    (responsibility === 'company' || driverId.length > 0) &&
    !create.isPending;

  const handleSubmit = async () => {
    try {
      await create.mutateAsync({
        vehicleId,
        driverId: responsibility === 'driver' && driverId ? driverId : undefined,
        description: description.trim(),
        severity,
        points: Number.parseInt(points, 10) || 0,
        amountCents: parseBRLToCents(amountReais),
        discountAmountCents: discountReais ? parseBRLToCents(discountReais) : undefined,
        infractionDate: new Date(`${infractionDate}T12:00:00`).toISOString(),
        dueDate: dueDate ? new Date(`${dueDate}T23:59:59`).toISOString() : undefined,
        location: location.trim() || undefined,
        infractionCode: infractionCode.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success('Multa registrada');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nova multa</DialogTitle>
          <DialogDescription>
            Registre o auto de infração e defina quem responde por ele.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vehicle">Veículo *</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger id="vehicle">
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>
                <SelectContent>
                  {(vehiclesQuery.data?.data ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.plate}
                      {(v.brand || v.model) && ` — ${[v.brand, v.model].filter(Boolean).join(' ')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="infractionDate">Data da infração *</Label>
              <Input
                id="infractionDate"
                type="date"
                value={infractionDate}
                onChange={(e) => setInfractionDate(e.target.value)}
              />
            </div>
          </div>

          {/* Responsabilidade */}
          <div className="space-y-2">
            <Label>Responsabilidade *</Label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                  responsibility === 'company'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent/40'
                }`}
              >
                <input
                  type="radio"
                  name="responsibility"
                  checked={responsibility === 'company'}
                  onChange={() => {
                    setResponsibility('company');
                    setDriverId('');
                  }}
                  className="size-4"
                />
                <div>
                  <p className="font-medium">Empresa</p>
                  <p className="text-xs text-muted-foreground">Frota assume a multa.</p>
                </div>
              </label>
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                  responsibility === 'driver'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent/40'
                }`}
              >
                <input
                  type="radio"
                  name="responsibility"
                  checked={responsibility === 'driver'}
                  onChange={() => setResponsibility('driver')}
                  className="size-4"
                />
                <div>
                  <p className="font-medium">Motorista</p>
                  <p className="text-xs text-muted-foreground">
                    Indicar condutor para transferência de pontos.
                  </p>
                </div>
              </label>
            </div>
            {responsibility === 'driver' && (
              <div className="space-y-1.5">
                <Label htmlFor="driver">Motorista responsável *</Label>
                <Select value={driverId} onValueChange={setDriverId}>
                  <SelectTrigger id="driver">
                    <SelectValue placeholder="Selecione o motorista" />
                  </SelectTrigger>
                  <SelectContent>
                    {(driversQuery.data?.data ?? []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} · CNH {d.cnhCategory}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Descrição da infração *</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: Excesso de velocidade - radar BR-116 km 42"
              maxLength={500}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="severity">Gravidade *</Label>
              <Select
                value={severity}
                onValueChange={(v) => handleSeverityChange(v as FineSeverity)}
              >
                <SelectTrigger id="severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SEVERITY_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="points">Pontos *</Label>
              <Input
                id="points"
                inputMode="numeric"
                value={points}
                onChange={(e) => setPoints(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="amount">Valor (R$) *</Label>
              <Input
                id="amount"
                inputMode="decimal"
                value={amountReais}
                onChange={(e) => setAmountReais(e.target.value)}
                placeholder="0,00"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="discount">Valor com desconto (R$)</Label>
              <Input
                id="discount"
                inputMode="decimal"
                value={discountReais}
                onChange={(e) => setDiscountReais(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dueDate">Vencimento</Label>
              <Input
                id="dueDate"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="location">Local</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ex.: BR-116 km 42 - Registro/SP"
                maxLength={500}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">Código DETRAN</Label>
              <Input
                id="code"
                value={infractionCode}
                onChange={(e) => setInfractionCode(e.target.value)}
                placeholder="Ex.: 745-10"
                maxLength={50}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Observações</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Opcional"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Registrando…' : 'Registrar multa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
